/**
 * The client's basic building blocks. Pages compose these instead of styling their own buttons, cards, and states.
 */
import type { ComponentPropsWithoutRef, ReactNode } from 'react'
import { Icon, type IconName } from '../icons.tsx'

type ButtonVariant = 'primary' | 'secondary' | 'ghost'

export type TipSide = 'top' | 'right' | 'bottom' | 'left'

export interface ButtonProps extends ComponentPropsWithoutRef<'button'> {
  /** `primary` is lime: the one action that matters most on screen. */
  variant?: ButtonVariant
  icon?: IconName
  /** Icon-only buttons need a label; it's their accessible name and their tooltip. */
  label?: string
  /** Where an icon-only button's tooltip appears. Default `top`. */
  tipSide?: TipSide
}

export function Button({
  variant = 'secondary',
  icon,
  label,
  tipSide = 'top',
  children,
  className,
  ...rest
}: ButtonProps) {
  const iconOnly = !children
  return (
    <button
      type="button"
      className={['btn', `btn-${variant}`, iconOnly ? 'btn-icon has-tip' : '', className].filter(Boolean).join(' ')}
      aria-label={iconOnly ? label : undefined}
      {...rest}
    >
      {icon && <Icon name={icon} />}
      {children}
      {iconOnly && label && <Tip side={tipSide}>{label}</Tip>}
    </button>
  )
}

/**
 * A tooltip for the element it's inside (give that element `has-tip`). Shown on hover and keyboard focus, after a
 * short delay. Hidden from assistive tech: the element's own label says the same thing.
 */
export function Tip({ side = 'top', children }: { side?: TipSide; children: ReactNode }) {
  return (
    <span className={`tip tip-${side}`} aria-hidden>
      {children}
    </span>
  )
}

/** A page's title row, with optional actions on the right. */
export function PageHeader({ title, meta, actions }: { title: ReactNode; meta?: ReactNode; actions?: ReactNode }) {
  return (
    <header className="page-header">
      <div>
        <h1 className="page-title">{title}</h1>
        {meta && <p className="page-meta">{meta}</p>}
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </header>
  )
}

/** A section within a page: a small label, then its content. */
export function Section({ label, aside, children }: { label: ReactNode; aside?: ReactNode; children: ReactNode }) {
  return (
    <section className="ui-section">
      <div className="ui-section-head">
        <h2 className="eyebrow">{label}</h2>
        {aside}
      </div>
      {children}
    </section>
  )
}

/** A bordered card; pass `href` to make the whole card a link. */
export function Card({ href, className, children }: { href?: string; className?: string; children: ReactNode }) {
  const cls = ['card', href ? 'card-link' : '', className].filter(Boolean).join(' ')
  return href ? (
    <a className={cls} href={href}>
      {children}
    </a>
  ) : (
    <div className={cls}>{children}</div>
  )
}

/** Nothing to show yet, with an optional next step. */
export function Empty({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      <p>{children}</p>
      {action}
    </div>
  )
}

export function Loading({ children = 'Loading…' }: { children?: ReactNode }) {
  return (
    <p className="loading" role="status">
      <Icon name="loading" className="spin" />
      {children}
    </p>
  )
}

export function ErrorNote({ error, onRetry }: { error: { message: string }; onRetry?: () => void }) {
  return (
    <div className="error-note" role="alert">
      <p>{error.message}</p>
      {onRetry && (
        <Button variant="ghost" icon="refresh" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  )
}

/** A small rounded label: status, counts, tags. */
export function Badge({
  tone = 'neutral',
  children,
}: {
  tone?: 'neutral' | 'live' | 'you' | 'featured'
  children: ReactNode
}) {
  return <span className={`badge badge-${tone}`}>{children}</span>
}

/** A round picture of a person or player: their image when they have one, else their initial. */
export function Avatar({
  name,
  src,
  size = 28,
}: {
  name: string | null
  src?: string | null | undefined
  size?: number
}) {
  const initial = name?.trim()[0]?.toUpperCase() ?? ''
  return (
    <span className="avatar" style={{ width: size, height: size, fontSize: size * 0.45 }} aria-hidden>
      {src ? <img src={src} alt="" /> : initial || <Icon name="user" size={size * 0.55} />}
    </span>
  )
}

export interface TabLink {
  id: string
  label: ReactNode
  href: string
  /** A count beside the label, once known. */
  count?: number | null
}

/** A row of tabs that are links (each tab is its own route), the current one underlined. */
export function Tabs({ tabs, current, label }: { tabs: readonly TabLink[]; current: string; label: string }) {
  return (
    <nav className="tabs" aria-label={label}>
      {tabs.map((t) => (
        <a key={t.id} className="tab" href={t.href} aria-current={t.id === current ? 'page' : undefined}>
          {t.label}
          {t.count != null && <span className="tab-count">{t.count}</span>}
        </a>
      ))}
    </nav>
  )
}
